import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { ContractCallBase } from '@stacks/connect';
import { cvToString, deserializeCV } from '@stacks/transactions';

import useNetwork from '../../../../hooks/use-network';
import { getContractInterface } from '../../../../utils/contract-interface';
import { getAccountExplorerUrl, getTxnExplorerUrl } from '../../../../utils/helper';
import { IMultisigTransaction } from '../../../../utils/transform-multisig-transaction';

import { useFunctionArgumentsStyles } from './useFunctionArgumentsStyles';

const format = (arg: string, argType: string | object) => {
  const argCV = deserializeCV(arg);
  const strValue = cvToString(argCV);

  if (typeof argType === 'string' && argType === 'trait_reference') {
    const address = strValue.split('.')[0];
    if (address) {
      return address.slice(0, 4) + '...' + address.slice(-4) + '.' + strValue.split('.')[1];
    }
  }
  if (strValue.startsWith('u')) return strValue.slice(1);

  return strValue;
};

const isPrincipalArg = (argType: string | object) =>
  typeof argType === 'string' && (argType === 'trait_reference' || argType === 'principal');

const isNumericArg = (arg: string) => cvToString(deserializeCV(arg)).startsWith('u');

const getPrincipalArgLink = (argValue: string, argType: string | object, network: string) => {
  const argCV = deserializeCV(argValue);
  const strValue = cvToString(argCV);

  if (typeof argType === 'string' && argType === 'trait_reference') {
    return getTxnExplorerUrl(strValue, network);
  } else {
    return getAccountExplorerUrl(strValue);
  }
};

type TypedFuncArg = { name: string; type: string | object };

const displayArgType = (argType: string | object) => {
  if (typeof argType === 'string') {
    return argType;
  } else {
    const firstKey = Object.keys(argType)[0];

    // @ts-ignore
    return `(${firstKey} ${argType[firstKey]})`;
  }
};

export const FunctionArguments: React.FC<{ transaction: IMultisigTransaction }> = ({
  transaction,
}) => {
  const classes = useFunctionArgumentsStyles();
  const [network] = useNetwork();
  const [typedFunctionArgs, setTypedFunctionArgs] = useState<TypedFuncArg[]>([]);
  const { contractAddress, contractName, functionName, functionArgs } =
    transaction.payload as ContractCallBase;

  useEffect(() => {
    if (transaction) {
      getContractInterface(
        (transaction.payload as ContractCallBase).contractName,
        (transaction.payload as ContractCallBase).contractAddress,
        network === 'testnet'
      ).then((res: { functions: any[] }) =>
        setTypedFunctionArgs((res.functions.find((f: any) => f.name === functionName) as any)?.args)
      );
    }
  }, [transaction, functionName]);

  return (
    <Box className={classes.container}>
      <p className={classes.title}>Function and arguments</p>

      <div className={classes.contractInfo}>
        <span className={classes.contractIcon} />
        <div className={classes.textContainer}>
          <p className={classes.functionName}>{functionName}</p>
          <p className={classes.contractAddr}>
            {contractAddress.slice(0, 4) + '...' + contractAddress.slice(-4)}.{contractName}
          </p>
        </div>
      </div>

      <hr className={classes.separator} />

      <dl className={classes.arguments}>
        {typedFunctionArgs.length &&
          functionArgs.map((arg, index) => (
            <div className={classes.arg} key={index}>
              <dt className={classes.argName}>{typedFunctionArgs[index].name}</dt>
              <dd className={classes.argValue}>
                {isPrincipalArg(typedFunctionArgs[index].type) ? (
                  <a
                    href={getPrincipalArgLink(
                      arg as string,
                      typedFunctionArgs[index].type,
                      network
                    )}
                    target="_blank"
                    rel="noreferrer"
                    className={classes.principalArg}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    {format(arg as string, typedFunctionArgs[index].type)}
                  </a>
                ) : (
                  <span
                    className={isNumericArg(arg as string) ? classes.numericArg : classes.plainArg}
                  >
                    {format(arg as string, typedFunctionArgs[index].type)}
                  </span>
                )}
                <span className={classes.argType}>
                  {displayArgType(typedFunctionArgs[index].type)}
                </span>
              </dd>
            </div>
          ))}
      </dl>
    </Box>
  );
};
